import type { OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core'
import { Component, ElementRef, ChangeDetectionStrategy, inject, output, viewChild, model, signal } from '@angular/core'
import { FileUpload } from '@awork/_shared/models/file-upload.model'
import { AutoUnsubscribe } from '@awork/_shared/decorators/auto-unsubscribe'
import type { Subscription } from 'rxjs'
import { FileModalService } from '../../../services/file-modal-service/file-modal.service'
import { FabButtonComponent } from '../../icon-buttons/fab-button/fab-button.component'
import { TooltipDirective } from '../../../directives/tooltip/tooltip.directive'
import { DotsLoaderComponent } from '../../ui-help/dots-loader/dots-loader.component'
import { Size } from '@awork/_shared/types/size'
import { FileSizePipe } from '../../../pipes/file-size/file-size.pipe'
import { FileIconComponent } from '../file-icon/file-icon.component'

@AutoUnsubscribe()
@Component({
  selector: 'aw-inline-file-preview',
  templateUrl: './inline-file-preview.component.html',
  styleUrls: ['./inline-file-preview.component.scss'],
  imports: [FileIconComponent, FabButtonComponent, TooltipDirective, DotsLoaderComponent, FileSizePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InlineFilePreviewComponent implements OnInit, OnChanges, OnDestroy {
  element = inject(ElementRef)
  private fileModalService = inject(FileModalService)

  readonly file = model<FileUpload>()

  readonly previewOpen = output<void>()
  readonly previewClose = output<void>()

  readonly downloadLinkElement = viewChild<ElementRef>('downloadLink')

  protected readonly Size = Size
  readonly isLoading = signal(false)
  protected readonly translations = q.translations.FilePreviewComponent
  protected readonly commonTranslations = q.translations.common
  readonly view = signal<'image' | 'icon' | 'externalFile'>('image')

  readonly previewUrl = signal<string | undefined>(undefined)

  protected userSubscription: Subscription

  ngOnInit(): void {
    if (this.file()) {
      this.previewUrl.set(this.file().mimeType?.includes('image') ? this.file().reducedImage : this.file().filePreview)

      this.initView()
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.file?.currentValue) {
      this.parseFileIfString()
    }
  }

  ngOnDestroy(): void {}

  /**
   * In case the component is being used as an element, the input is passed
   * as a JSON.stringify string
   */
  private parseFileIfString(): void {
    const file = this.file()

    if (typeof file === 'string') {
      try {
        this.file.set(new FileUpload(JSON.parse(file)))
      } catch (e) {
        // TODO: add logdna
      }
    }
  }

  /**
   * Inits the view according to the file type
   */
  initView(): void {
    const file = this.file()

    if (file.externalFileUrl) {
      this.view.set('externalFile')

      return
    }

    const isImage = this.previewUrl() && file.mimeType && FileUpload.isImageMimeType(file.mimeType)

    if (isImage) {
      this.isLoading.set(true)
      this.view.set('image')
    } else {
      this.view.set('icon')
    }
  }

  /**
   * Show Image after loading successfully and hide file Icon
   */
  imageLoaded(): void {
    this.isLoading.set(false)
  }

  /**
   * Handle broken images and show file Icon
   */
  imageLoadingError(): void {
    this.isLoading.set(false)
    this.view.set('icon')
  }

  /**
   * Opens the carousel with the current selected file
   */
  onView(): void {
    this.previewOpen.emit()

    const previewHiding$ = this.fileModalService.showFilePreviewLightboxOverlay(this.file(), null, null, false)

    previewHiding$.subscribe(() => this.previewClose.emit())
  }
}
